import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  getReviews,
  deleteReview,
  reorderReviews,
  type Review,
} from '../../features/reviews';
import { DeleteConfirmModal, DashboardPageHeader } from '../../shared/ui';
import { getUserFriendlyErrorMessage, logErrorInDev } from '../../shared/lib';
import ReviewModal from '../../components/dashboard/ReviewModal';

const ReviewsPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [items, setItems] = useState<Review[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Review | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Review | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [isReordering, setIsReordering] = useState(false);

  const canView = !!profile && can(profile, 'reviews', 'view');
  const canEdit = !!profile && can(profile, 'reviews', 'edit');
  const canDelete = !!profile && can(profile, 'reviews', 'delete');

  useEffect(() => {
    if (!canView) return;
    void loadItems();
  }, [canView]);

  const loadItems = async () => {
    try {
      const data = await getReviews();
      setItems(data);
    } catch (err) {
      logErrorInDev('Failed to load reviews', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to load reviews.'), 'error');
    }
  };

  const handleSaved = () => {
    showToast('Review saved.');
    void loadItems();
    setShowModal(false);
    setEditingItem(null);
  };

  const handleEdit = (item: Review) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleDeleteClick = (item: Review) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteReview(itemToDelete.id);
      setItems((prev) => prev.filter((i) => i.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
      showToast('Review deleted.');
    } catch (err) {
      logErrorInDev('Failed to delete review', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to delete review.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = items.filter((item) => {
      const matchesSearch =
        !term ||
        item.author.toLowerCase().includes(term) ||
        item.quote.toLowerCase().includes(term);
      const matchesFilter =
        filterActive === 'all' ||
        (filterActive === 'active' && item.isActive) ||
        (filterActive === 'inactive' && !item.isActive);
      return matchesSearch && matchesFilter;
    });
    return filtered.sort((a, b) => a.displayOrder - b.displayOrder);
  }, [items, searchTerm, filterActive]);

  const moveItem = async (index: number, direction: 'up' | 'down') => {
    if (isReordering) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= filteredItems.length) return;

    setIsReordering(true);
    try {
      const newItems = [...filteredItems];
      [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
      const updatedItems = newItems.map((item, i) => ({ ...item, displayOrder: i }));
      setItems(updatedItems);
      await reorderReviews({ ids: updatedItems.map((i) => i.id) });
      showToast('Review order saved.');
    } catch (err) {
      logErrorInDev('Failed to reorder reviews', err);
      showToast(getUserFriendlyErrorMessage(err, 'Failed to save review order.'), 'error');
      await loadItems();
    } finally {
      setIsReordering(false);
    }
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view reviews.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <DashboardPageHeader
        title="Reviews"
        description="Manage the home page testimonials carousel."
        icon="bi bi-chat-quote"
        actions={
          canEdit ? (
            <button
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={() => {
                setEditingItem(null);
                setShowModal(true);
              }}
            >
              <i className="bi bi-plus-circle" />
              Add Review
            </button>
          ) : null
        }
      />

      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-6">
                  <label className="form-label text-muted small mb-1">Search</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search by author or quote..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label text-muted small mb-1">Filter by Status</label>
                  <select
                    className="form-select"
                    value={filterActive}
                    onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
                  >
                    <option value="all">All</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive only</option>
                  </select>
                </div>
                <div className="col-md-2 text-md-end">
                  <span className="text-muted small">
                    {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {filteredItems.map((item, index) => (
          <div key={item.id} className="col-12 col-md-6 col-xl-4">
            <div className={`card h-100 border-0 shadow-sm ${!item.isActive ? 'opacity-75' : ''}`}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="min-w-0">
                    <div className="fw-semibold">{item.author}</div>
                    <div className="small text-muted">Order: {item.displayOrder}</div>
                  </div>
                  <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <blockquote className="small text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  “{item.quote}”
                </blockquote>
              </div>
              <div className="card-footer bg-white border-top-0 d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  {canEdit && (
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(item)}>
                      <i className="bi bi-pencil me-1" />
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(item)}>
                      <i className="bi bi-trash me-1" />
                      Delete
                    </button>
                  )}
                </div>
                {canEdit && filteredItems.length > 1 && (
                  <div className="btn-group btn-group-sm">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => void moveItem(index, 'up')}
                      disabled={index === 0 || isReordering}
                      title="Move up"
                    >
                      <i className="bi bi-arrow-up" />
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => void moveItem(index, 'down')}
                      disabled={index === filteredItems.length - 1 || isReordering}
                      title="Move down"
                    >
                      <i className="bi bi-arrow-down" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <i className="bi bi-chat-quote text-muted" style={{ fontSize: '3rem' }} />
              <p className="text-muted mt-3">
                {searchTerm || filterActive !== 'all'
                  ? 'No reviews match your search/filter.'
                  : 'No reviews yet. Click “Add Review” to create one.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <ReviewModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onReviewSaved={handleSaved}
        item={editingItem}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        itemName={itemToDelete?.author ?? ''}
        itemType="Review"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ReviewsPage;

